import type { Ride } from "../types/ride";
import type { PreferredUnits } from "../../athlete-profile/types/athleteProfile";
import {
  formatDistanceFromMeters,
  formatElevationFromMeters,
  formatSpeedFromMetersPerSecond
} from "../../../lib/unitPreferences";

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

function formatTimestamp(value: unknown): string | null {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" });
}

function formatDuration(value: unknown): string | null {
  const numericValue = toNumber(value);
  if (numericValue === null || numericValue <= 0) {
    return null;
  }

  const hours = Math.floor(numericValue / 3600);
  const minutes = Math.floor((numericValue % 3600) / 60);
  const seconds = Math.floor(numericValue % 60);

  const parts = [] as string[];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(" ");
}

function formatDistance(value: unknown, preferredUnits: PreferredUnits): string | null {
  const numericValue = toNumber(value);
  if (numericValue === null || numericValue <= 0) {
    return null;
  }
  return formatDistanceFromMeters(numericValue, preferredUnits);
}

function formatAverageSpeed(value: unknown, preferredUnits: PreferredUnits): string | null {
  const numericValue = toNumber(value);
  if (numericValue === null || numericValue <= 0) {
    return null;
  }
  return formatSpeedFromMetersPerSecond(numericValue, preferredUnits);
}

function formatElevationGain(value: unknown, preferredUnits: PreferredUnits): string | null {
  const numericValue = toNumber(value);
  if (numericValue === null || numericValue <= 0) {
    return null;
  }
  return formatElevationFromMeters(numericValue, preferredUnits);
}

function formatMetric(value: unknown, unit: string, decimals = 1): string | null {
  const numericValue = toNumber(value);
  if (numericValue === null || numericValue <= 0) {
    return null;
  }

  return `${numericValue.toFixed(decimals)} ${unit}`.trim();
}

function formatVehicleCount(value: unknown): string | null {
  const numericValue = toNumber(value);
  if (numericValue === null || numericValue < 0 || !Number.isInteger(numericValue)) {
    return null;
  }

  return `${Math.round(numericValue)}`;
}

function formatHeatStrainIndex(value: number[]): string | null {
  if (value.length === 0) {
    return null;
  }

  const average = value.reduce((sum, item) => sum + item, 0) / value.length;
  const maximum = Math.max(...value);
  return value.length > 1 ? `${average.toFixed(1)} / ${maximum.toFixed(1)}` : `${average.toFixed(1)}`;
}

export function getRideSummaryRows(ride: Ride, preferredUnits: PreferredUnits = "imperial"): Array<[string, string]> {
  return [
    ["Ride date", formatTimestamp(ride.rideDate) ?? "--"],
    ["Moving Time", formatDuration(ride.movingTime) ?? "--"],
    ["Elapsed Time", formatDuration(ride.elapsedTime) ?? "--"],
    ["Distance", formatDistance(ride.distance, preferredUnits) ?? "--"],
    ["Average speed", formatAverageSpeed(ride.averageSpeed, preferredUnits) ?? "--"],
    ["Elevation gain", formatElevationGain(ride.elevationGain, preferredUnits) ?? "--"],
    ["Average heart rate", formatMetric(ride.averageHeartRate, "bpm", 0) ?? "--"],
    ["Max heart rate", formatMetric(ride.maxHeartRate, "bpm", 0) ?? "--"],
    ["Average cadence", formatMetric(ride.averageCadence, "rpm", 0) ?? "--"],
    ["Average power", formatMetric(ride.averagePower, "W", 0) ?? "--"],
    ["Normalized power", formatMetric(ride.rideAnalytics?.normalizedPower ?? ride.normalizedPower, "W", 0) ?? "--"],
    ["IF", formatMetric(ride.rideAnalytics?.intensityFactor ?? ride.if, "", 2) ?? "--"],
    ["TSS", formatMetric(ride.rideAnalytics?.trainingStressScore ?? ride.tss, "", 0) ?? "--"],
    ["Work", formatMetric(ride.rideAnalytics?.workKilojoules, "kJ", 0) ?? "--"],
    ["Maximum power", formatMetric(ride.maximumPower, "W", 0) ?? "--"],
    ["Calories", formatMetric(ride.calories, "kcal", 0) ?? "--"],
    ["HSI", formatHeatStrainIndex(ride.heatStrainIndexValues) ?? "--"],
    ["Vehicle count", formatVehicleCount(ride.vehicleCount) ?? "--"]
  ];
}
